import { Check, Copy, Edit2 } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';

import {
  AddressAutocomplete,
  StateCodeSelect,
} from '@/components/ui/address-autocomplete';
import { Button } from '@/components/ui/button';
import {
  type CarrierDivision,
  useListDivisionsQuery,
} from '@/services/api/carriersApiService';

import { HANDLE_TYPE_ICONS, SOURCE_COLORS, SOURCE_LABELS } from './constants';
import type { ContactHandle, HandleType } from './types';

interface ContactHandleRowProps {
  handle: ContactHandle;
  onUpdate: (updates: Partial<ContactHandle>) => void;
  onCancel?: () => void;
  carrierId?: string;
  startInEditMode?: boolean;
  canEdit?: boolean;
}

const formatAddress = (handle: ContactHandle): string => {
  const parts = [handle.addressLine1];
  if (handle.addressLine2) parts.push(handle.addressLine2);
  if (handle.city && handle.state && handle.zip) {
    parts.push(`${handle.city}, ${handle.state} ${handle.zip}`);
  }
  return parts.filter(Boolean).join(', ');
};

const switchHandleType = (
  current: ContactHandle,
  newType: HandleType,
): ContactHandle => {
  return {
    ...current,
    handleType: newType,
    value: newType !== 'MAILING_ADDRESS' ? current.value : null,
    addressLine1: newType === 'MAILING_ADDRESS' ? current.addressLine1 : null,
    addressLine2: newType === 'MAILING_ADDRESS' ? current.addressLine2 : null,
    city: newType === 'MAILING_ADDRESS' ? current.city : null,
    state: newType === 'MAILING_ADDRESS' ? current.state : null,
    zip: newType === 'MAILING_ADDRESS' ? current.zip : null,
    country: newType === 'MAILING_ADDRESS' ? current.country || 'US' : null,
  };
};

const HANDLE_TYPE_OPTIONS: { value: HandleType; label: string }[] = [
  { value: 'PHONE', label: 'Phone' },
  { value: 'FAX', label: 'Fax' },
  { value: 'EMAIL', label: 'Email' },
  { value: 'WEBSITE', label: 'Website' },
  { value: 'MAILING_ADDRESS', label: 'Address' },
];

export const ContactHandleRow = ({
  handle,
  onUpdate,
  onCancel,
  carrierId,
  startInEditMode = false,
  canEdit = false,
}: ContactHandleRowProps) => {
  const [isEditing, setIsEditing] = useState(startInEditMode);
  const [localHandle, setLocalHandle] = useState(handle);
  const [copied, setCopied] = useState(false);
  const Icon = HANDLE_TYPE_ICONS[handle.handleType];
  const { data: divisions } = useListDivisionsQuery(
    { carrierId: carrierId as string },
    { skip: !carrierId },
  );

  useEffect(() => {
    setLocalHandle(handle);
  }, [handle]);

  const handleEdit = () => {
    setLocalHandle(handle);
    setIsEditing(true);
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      setLocalHandle(handle);
      setIsEditing(false);
    }
  };

  const handleSave = () => {
    if (handle.id === 'new' && !localHandle.divisionId) {
      toast.error('Please select a division');
      return;
    }
    onUpdate(localHandle);
    setIsEditing(false);
  };

  const handleCopy = async () => {
    const valueToCopy =
      handle.handleType === 'MAILING_ADDRESS'
        ? formatAddress(handle)
        : handle.value || '';

    await navigator.clipboard.writeText(valueToCopy);
    setCopied(true);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleAddressSelect = useCallback(
    (address: {
      address_line1: string;
      address_line2: string | null;
      city: string;
      state: string;
      postal_code: string;
    }) => {
      setLocalHandle((prev) => ({
        ...prev,
        addressLine1: address.address_line1,
        addressLine2: address.address_line2 || '',
        city: address.city,
        state: address.state,
        zip: address.postal_code,
      }));
    },
    [],
  );

  if (isEditing) {
    const fieldId = `handle-${handle.id}`;

    return (
      <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
        <div className="space-y-4">
          {/* Type selector pills */}
          <div className="space-y-1.5">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide block">
              Type
            </span>
            <div className="flex flex-wrap gap-1.5">
              {HANDLE_TYPE_OPTIONS.map((option) => {
                const OptionIcon = HANDLE_TYPE_ICONS[option.value];
                const isSelected = localHandle.handleType === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() =>
                      setLocalHandle(
                        switchHandleType(localHandle, option.value),
                      )
                    }
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border transition-all ${
                      isSelected
                        ? 'bg-primary text-white border-primary'
                        : 'bg-gray-50 text-gray-700 border-gray-200 hover:border-gray-300 hover:bg-gray-100'
                    }`}
                  >
                    <OptionIcon className="w-3.5 h-3.5" />
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Label */}
          <div className="space-y-1.5">
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label
              htmlFor={`${fieldId}-label`}
              className="text-xs font-medium text-gray-500 uppercase tracking-wide"
            >
              Label
            </label>
            <input
              id={`${fieldId}-label`}
              type="text"
              placeholder="e.g., Florida BI Claims, Main Office..."
              value={localHandle.name}
              onChange={(e) =>
                setLocalHandle({ ...localHandle, name: e.target.value })
              }
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
            />
          </div>

          {/* Value or Address Fields */}
          {localHandle.handleType === 'MAILING_ADDRESS' ? (
            <div className="space-y-3">
              <div className="space-y-1.5">
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label
                  htmlFor={`${fieldId}-address1`}
                  className="text-xs font-medium text-gray-500 uppercase tracking-wide"
                >
                  Address
                </label>
                <AddressAutocomplete
                  id={`${fieldId}-address1`}
                  value={localHandle.addressLine1 || ''}
                  onChange={(value) =>
                    setLocalHandle({ ...localHandle, addressLine1: value })
                  }
                  onAddressSelect={handleAddressSelect}
                  placeholder="Start typing an address..."
                />
              </div>

              <input
                id={`${fieldId}-address2`}
                type="text"
                placeholder="Apt, Suite, Unit (optional)"
                value={localHandle.addressLine2 || ''}
                onChange={(e) =>
                  setLocalHandle({
                    ...localHandle,
                    addressLine2: e.target.value,
                  })
                }
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
              />

              <div className="grid grid-cols-3 gap-2">
                <input
                  id={`${fieldId}-city`}
                  type="text"
                  placeholder="City"
                  value={localHandle.city || ''}
                  onChange={(e) =>
                    setLocalHandle({ ...localHandle, city: e.target.value })
                  }
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                />
                <StateCodeSelect
                  id={`${fieldId}-state`}
                  value={localHandle.state || ''}
                  onChange={(code) =>
                    setLocalHandle({ ...localHandle, state: code })
                  }
                  placeholder="State"
                  className="w-full h-[38px]"
                />
                <input
                  id={`${fieldId}-zip`}
                  type="text"
                  placeholder="ZIP"
                  value={localHandle.zip || ''}
                  onChange={(e) =>
                    setLocalHandle({ ...localHandle, zip: e.target.value })
                  }
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                />
              </div>
            </div>
          ) : (
            <div className="space-y-1.5">
              <label
                htmlFor={`${fieldId}-value`}
                className="text-xs font-medium text-gray-500 uppercase tracking-wide"
              >
                {localHandle.handleType === 'PHONE'
                  ? 'Phone Number'
                  : localHandle.handleType === 'FAX'
                    ? 'Fax Number'
                    : localHandle.handleType === 'EMAIL'
                      ? 'Email Address'
                      : 'URL'}
              </label>
              <input
                id={`${fieldId}-value`}
                type={localHandle.handleType === 'EMAIL' ? 'email' : 'text'}
                placeholder={
                  localHandle.handleType === 'PHONE'
                    ? '(555) 123-4567'
                    : localHandle.handleType === 'EMAIL'
                      ? 'claims@example.com'
                      : localHandle.handleType === 'WEBSITE'
                        ? 'https://example.com'
                        : '(555) 123-4567'
                }
                value={localHandle.value || ''}
                onChange={(e) =>
                  setLocalHandle({ ...localHandle, value: e.target.value })
                }
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
              />
            </div>
          )}

          {/* Division Selector */}
          {Array.isArray(divisions) && divisions.length > 0 && (
            <div className="space-y-1.5">
              <label
                htmlFor={`${fieldId}-division`}
                className="text-xs font-medium text-gray-500 uppercase tracking-wide"
              >
                Division{' '}
                {handle.id === 'new' && <span className="text-error">*</span>}
              </label>
              <select
                id={`${fieldId}-division`}
                value={localHandle.divisionId || ''}
                onChange={(e) =>
                  setLocalHandle({
                    ...localHandle,
                    divisionId: e.target.value || null,
                  })
                }
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none bg-white"
              >
                <option value="" disabled={handle.id === 'new'}>
                  {handle.id === 'new' ? 'Select a division' : '(none)'}
                </option>
                {divisions.map((d: CarrierDivision) => (
                  <option key={d.id} value={d.id}>
                    {d.divisionName}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Description */}
          <div className="space-y-1.5">
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label
              htmlFor={`${fieldId}-description`}
              className="text-xs font-medium text-gray-500 uppercase tracking-wide"
            >
              Notes{' '}
              <span className="font-normal text-gray-400">(optional)</span>
            </label>
            <input
              id={`${fieldId}-description`}
              type="text"
              placeholder="Any additional context..."
              value={localHandle.description || ''}
              onChange={(e) =>
                setLocalHandle({ ...localHandle, description: e.target.value })
              }
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" size="sm" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleSave}
              disabled={handle.id === 'new' && !localHandle.divisionId}
            >
              {handle.id === 'new' ? 'Add Contact' : 'Save Changes'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Read-only display
  const displayValue =
    handle.handleType === 'MAILING_ADDRESS'
      ? formatAddress(handle)
      : handle.value || '';

  return (
    <div className="group flex items-center gap-3 p-3 bg-white border border-gray-100 rounded-lg hover:border-gray-200 hover:shadow-sm transition-all">
      {/* Icon */}
      <div className="flex-shrink-0 w-8 h-8 bg-gray-50 rounded-lg flex items-center justify-center">
        <Icon className="w-4 h-4 text-gray-500" />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        {handle.name && (
          <div className="text-sm font-medium text-gray-900 truncate">
            {handle.name}
          </div>
        )}
        <div className="text-sm text-gray-600 truncate">{displayValue}</div>
        {handle.description && (
          <div className="text-xs text-gray-400 mt-0.5 truncate">
            {handle.description}
          </div>
        )}
      </div>

      {/* Source Badge */}
      {handle.source && (
        <span
          className={`flex-shrink-0 px-2 py-0.5 text-[10px] font-medium rounded border ${
            SOURCE_COLORS[handle.source]
          }`}
        >
          {SOURCE_LABELS[handle.source]}
        </span>
      )}

      {/* Actions */}
      <div className="flex-shrink-0 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          type="button"
          onClick={handleCopy}
          className="p-1.5 text-gray-400 hover:text-primary hover:bg-primary/5 rounded-md transition-colors"
          title="Copy"
        >
          {copied ? (
            <Check className="w-3.5 h-3.5 text-success" />
          ) : (
            <Copy className="w-3.5 h-3.5" />
          )}
        </button>
        {canEdit && (
          <button
            type="button"
            onClick={handleEdit}
            className="p-1.5 text-gray-400 hover:text-primary hover:bg-primary/5 rounded-md transition-colors"
            title="Edit"
          >
            <Edit2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
};
